import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import { Groups, Add } from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '@api/endpoints/chat'
// Note: chat unread count is computed from conversation data, not the chatUnreadCount API
import { ChatMessageDto, ConversationType, NotificationMode, resolveNotificationMode } from '@api/types/chat'
import { useChatStore, useActiveConversation } from '@store/chatStore'
import { useAuthStore } from '@store/authStore'
import { useGeneralChatConnection } from '@hooks/useSignalR'
import { ConversationList } from '../components/ConversationList'
import { MessageThread } from '../components/MessageThread'
import { MessageInput } from '../components/MessageInput'
import { ChatHeader } from '../components/ChatHeader'
import { NewChatDialog } from '../components/NewChatDialog'
import { AdventureContextBanner } from '../components/AdventureContextBanner'

const SIDEBAR_WIDTH = 300

export default function ChatPage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuthStore()

  const [newChatDialogOpen, setNewChatDialogOpen] = useState(false)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list')

  const {
    setConversations,
    setActiveConversation,
    activeConversationId,
    setMessages,
    addMessage,
    markConversationRead,
    setUnreadCount,
    setMessagesLoading,
    appendMessages,
    removeConversation,
    unreadCount: chatUnreadCount,
  } = useChatStore()

  const activeConversation = useActiveConversation()

  // SignalR connection
  const {
    isConnected,
    joinConversation,
    sendTypingIndicator,
    sendMessageToConversation,
    onNewMessage,
    onGroupMembershipChanged,
    onRemovedFromConversation,
    onParticipantRemoved,
    onGroupNotification,
    leaveGroupChat,
    onConversationUpdateSignal,
    onTypingIndicator,
  } = useGeneralChatConnection()

  // Typing indicator state
  const [typingUsers, setTypingUsers] = useState<Map<string, { userName: string; timeout: ReturnType<typeof setTimeout> }>>(new Map())

  // Fetch conversations
  const {
    data: conversations,
    isLoading: conversationsLoading,
    error: conversationsError,
  } = useQuery({
    queryKey: ['chatConversations'],
    queryFn: chatApi.getConversations,
  })

  // Update store when conversations load
  // Preserve unreadMessageCount=0 for the active conversation to avoid race
  // condition where API returns stale unread count before markAsRead completes
  useEffect(() => {
    if (conversations) {
      const activeId = useChatStore.getState().activeConversationId
      if (activeId) {
        const merged = conversations.map((conv) =>
          conv.id === activeId && conv.unreadMessageCount > 0
            ? { ...conv, unreadMessageCount: 0 }
            : conv
        )
        setConversations(merged)
      } else {
        setConversations(conversations)
      }
    }
  }, [conversations, setConversations])

  // Handle URL parameter for opening specific conversation
  useEffect(() => {
    const conversationId = searchParams.get('conversation')
    if (conversationId && conversations) {
      setActiveConversation(conversationId)
      if (isMobile) {
        setMobileView('chat')
      }
      // Clear the URL parameter
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, conversations, setActiveConversation, isMobile, setSearchParams])

  // Fetch messages for active conversation
  const { data: messagesData } = useQuery({
    queryKey: ['chatMessages', activeConversationId],
    queryFn: () => chatApi.getMessages(activeConversationId!, 1, 50),
    enabled: !!activeConversationId,
  })

  // Update store when messages load
  useEffect(() => {
    if (messagesData && activeConversationId) {
      // API returns array directly, reverse to show oldest first
      const messages = Array.isArray(messagesData) ? [...messagesData].reverse() : []
      setMessages(
        activeConversationId,
        messages,
        1,
        false // Assume no more pages for now
      )
    }
  }, [messagesData, activeConversationId, setMessages])

  // Compute unread count from store conversations (not API data, to respect local markAsRead)
  const storeConversations = useChatStore((s) => s.conversations)
  useEffect(() => {
    const total = storeConversations.reduce((sum, c) => sum + (c.unreadMessageCount || 0), 0)
    setUnreadCount(total)
  }, [storeConversations, setUnreadCount])

  // Join conversation room and mark as read when switching conversations
  // NOTE: Do NOT call leaveConversation here — MainLayout manages room membership
  // for all conversations. Calling leave here would remove the connection from the
  // SignalR group entirely (no reference counting), breaking signals for that room.
  useEffect(() => {
    if (!isConnected || !activeConversationId) return

    joinConversation(activeConversationId)

    // Mark as read
    chatApi.markAsRead(activeConversationId)
    markConversationRead(activeConversationId)
    queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
  }, [isConnected, activeConversationId, joinConversation, markConversationRead, queryClient])

  // Handle real-time messages
  useEffect(() => {
    if (!isConnected) return

    const unsubscribe = onNewMessage((message) => {
      const currentActiveId = useChatStore.getState().activeConversationId
      addMessage(message.conversationId, message)

      if (message.conversationId === currentActiveId) {
        // Active conversation - mark as read so others see our read receipt
        chatApi.markAsRead(currentActiveId)
        markConversationRead(currentActiveId)
      }

      // Always refresh conversation list to update last message preview and unread counts
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
    })

    return unsubscribe
  }, [isConnected, onNewMessage, addMessage, queryClient, markConversationRead])

  // Handle group membership changes (when current user is removed/banned/left)
  useEffect(() => {
    if (!isConnected) return

    const unsubscribe = onGroupMembershipChanged((event) => {
      console.log('Group membership changed:', event)

      if (event.action === 'removed' || event.action === 'left' || event.action === 'banned') {
        // Leave the SignalR group
        leaveGroupChat(event.groupId)

        // Find and close any conversation for this group
        const groupConversation = conversations?.find(c => c.groupId === event.groupId)
        if (groupConversation) {
          // If this was the active conversation, deselect it
          if (activeConversationId === groupConversation.id) {
            setActiveConversation(null)
            if (isMobile) {
              setMobileView('list')
            }
          }
        }

        // Refresh conversations list
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })

        // Show notification
        const actionText = event.action === 'banned' ? 'banned from' :
                         event.action === 'removed' ? 'removed from' : 'left'
        console.log(`You were ${actionText} a group${event.reason ? `: ${event.reason}` : ''}`)
      }
    })

    return unsubscribe
  }, [isConnected, onGroupMembershipChanged, leaveGroupChat, conversations, activeConversationId, setActiveConversation, isMobile, queryClient])

  // Handle being removed from a conversation
  useEffect(() => {
    if (!isConnected) return

    const unsubscribe = onRemovedFromConversation((event) => {
      console.log('Removed from conversation:', event)

      // If this was the active conversation, deselect it
      if (activeConversationId === event.conversationId) {
        setActiveConversation(null)
        if (isMobile) {
          setMobileView('list')
        }
      }

      // Refresh conversations list
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })

      console.log(`You were removed from conversation: ${event.reason}`)
    })

    return unsubscribe
  }, [isConnected, onRemovedFromConversation, activeConversationId, setActiveConversation, isMobile, queryClient])

  // Handle participant removed (update participant list)
  useEffect(() => {
    if (!isConnected) return

    const unsubscribe = onParticipantRemoved((event) => {
      console.log('Participant removed:', event)

      // Refresh the conversation to update participant list
      if (activeConversationId === event.conversationId) {
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      }
    })

    return unsubscribe
  }, [isConnected, onParticipantRemoved, activeConversationId, queryClient])

  // Handle group permission changes
  useEffect(() => {
    if (!isConnected) return

    const unsubscribe = onGroupNotification((event) => {
      if (event.type === 'PermissionsChanged' && event.accessChatLevel !== undefined) {
        console.log('Group permissions changed:', event)

        // Refresh conversations to re-evaluate access
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
        queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      }
    })

    return unsubscribe
  }, [isConnected, onGroupNotification, queryClient])

  // Handle typing indicators
  useEffect(() => {
    if (!isConnected) return

    const unsubscribe = onTypingIndicator((indicator) => {
      const currentActiveId = useChatStore.getState().activeConversationId
      // Only show typing for the active conversation and not from ourselves
      if (indicator.conversationId !== currentActiveId || indicator.userId === user?.id) {
        return
      }

      setTypingUsers((prev) => {
        const newMap = new Map(prev)

        if (indicator.isTyping) {
          // Clear existing timeout if any
          const existing = newMap.get(indicator.userId)
          if (existing) {
            clearTimeout(existing.timeout)
          }

          // Set new timeout (5 seconds)
          const timeout = setTimeout(() => {
            setTypingUsers((current) => {
              const updated = new Map(current)
              updated.delete(indicator.userId)
              return updated
            })
          }, 5000)

          newMap.set(indicator.userId, {
            userName: indicator.userName,
            timeout,
          })
        } else {
          // User stopped typing
          const existing = newMap.get(indicator.userId)
          if (existing) {
            clearTimeout(existing.timeout)
          }
          newMap.delete(indicator.userId)
        }

        return newMap
      })
    })

    return unsubscribe
  }, [isConnected, onTypingIndicator, user?.id])

  // Clear typing users when conversation changes
  useEffect(() => {
    setTypingUsers((prev) => {
      prev.forEach(({ timeout }) => clearTimeout(timeout))
      return new Map()
    })
  }, [activeConversationId])

  // Handle conversation update signals (fetch from API instead of receiving full message)
  // Read activeConversationId from store at invocation time to avoid re-registering
  // the handler every time the user switches conversations (which causes listener accumulation)
  useEffect(() => {
    if (!isConnected) return

    // Dedup replayed signals (server may replay buffered signals on reconnect)
    const processedSignals = new Set<string>()

    const unsubscribe = onConversationUpdateSignal((signal) => {
      const signalKey = `${signal.updateType}:${signal.conversationId}:${signal.timestamp}`
      if (processedSignals.has(signalKey)) return
      processedSignals.add(signalKey)

      const currentActiveId = useChatStore.getState().activeConversationId

      switch (signal.updateType) {
        case 'NewMessage':
          // Fetch fresh conversations and update both React Query cache and store
          chatApi.getConversations().then((fresh) => {
            // Update React Query cache so the conversations useEffect doesn't overwrite with stale data
            queryClient.setQueryData(['chatConversations'], fresh)
          }).catch((err) => console.error('[ChatPage] Failed to refresh conversations:', err))

          // Also refresh messages for the active conversation
          if (signal.conversationId === currentActiveId) {
            chatApi.getMessages(currentActiveId, 1, 50).then((msgs) => {
              const messages = Array.isArray(msgs) ? [...msgs].reverse() : []
              useChatStore.getState().setMessages(currentActiveId, messages, 1, false)
            }).catch((err) => console.error('[ChatPage] Failed to refresh messages:', err))
          }
          break

        case 'MessagesRead':
        case 'MessageDeleted':
        case 'ReactionChanged':
          if (signal.conversationId === currentActiveId) {
            chatApi.getMessages(currentActiveId, 1, 50).then((msgs) => {
              const messages = Array.isArray(msgs) ? [...msgs].reverse() : []
              useChatStore.getState().setMessages(currentActiveId, messages, 1, false)
            }).catch((err) => console.error('[ChatPage] Failed to refresh messages:', err))
          }
          break

        case 'ParticipantLeft': {
          // Refresh conversations to update participant count
          chatApi.getConversations().then((fresh) => {
            queryClient.setQueryData(['chatConversations'], fresh)
          }).catch((err) => console.error('[ChatPage] Failed to refresh conversations:', err))

          // Inject a local system message into the active conversation
          if (signal.conversationId === currentActiveId) {
            // Look up the participant name from current conversation data
            const conv = useChatStore.getState().conversations.find(c => c.id === signal.conversationId)
            const participant = conv?.participants.find(p => p.userId === signal.senderId)
            const name = participant?.displayName || 'A member'

            const systemMessage: ChatMessageDto = {
              id: `system-left-${signal.senderId}-${signal.timestamp}`,
              conversationId: signal.conversationId,
              senderId: signal.senderId,
              senderName: name,
              senderAvatarUrl: null,
              content: `${name} left the conversation`,
              timestamp: signal.timestamp,
              isRead: true,
              isDeleted: false,
              isEdited: false,
              editedAt: null,
              isSystemMessage: true,
            }
            useChatStore.getState().addMessage(signal.conversationId, systemMessage)

            // Also refresh messages to get server-side state
            chatApi.getMessages(currentActiveId, 1, 50).then((msgs) => {
              const messages = Array.isArray(msgs) ? [...msgs].reverse() : []
              useChatStore.getState().setMessages(currentActiveId, messages, 1, false)
            }).catch((err) => console.error('[ChatPage] Failed to refresh messages:', err))
          }
          break
        }
      }
    })

    return unsubscribe
  }, [isConnected, onConversationUpdateSignal, queryClient])

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({ content, imageId }: { content: string; imageId?: string }) =>
      chatApi.sendMessage(activeConversationId!, { content, imageId }),
    onSuccess: (message) => {
      addMessage(activeConversationId!, message)
    },
  })

  // Load more messages
  const loadMoreMessages = async () => {
    if (!activeConversationId) return
    const currentMessages = useChatStore.getState().messages[activeConversationId]
    if (!currentMessages?.hasMore || currentMessages.loading) return

    setMessagesLoading(activeConversationId, true)
    try {
      const nextPage = currentMessages.page + 1
      const pageSize = 50
      const response = await chatApi.getMessages(activeConversationId, nextPage, pageSize)
      appendMessages(activeConversationId, response, nextPage, response.length === pageSize)
    } catch (error) {
      console.error('Error loading more messages:', error)
      setMessagesLoading(activeConversationId, false)
    }
  }

  const handleConversationSelect = (conversationId: string) => {
    setActiveConversation(conversationId)
    if (isMobile) {
      setMobileView('chat')
    }
  }

  const handleBack = () => {
    setMobileView('list')
    setActiveConversation(null)
  }

  const handleSendMessage = async (content: string, imageId?: string) => {
    console.log('[ChatPage] handleSendMessage:', { activeConversationId, contentLength: content.length, imageId })
    if (!activeConversationId) return

    // Send via REST API to persist the message
    // The API will send a SignalR signal to notify other participants
    console.log('[ChatPage] Sending message via REST API mutation')
    sendMessageMutation.mutate({ content, imageId })
  }

  const handleImageUpload = async (file: File): Promise<string | undefined> => {
    try {
      const response = await chatApi.uploadImage(file)
      return response.imageId
    } catch (error) {
      console.error('Failed to upload image:', error)
      throw error
    }
  }

  const handleTyping = (isTyping: boolean) => {
    if (!activeConversationId) return
    sendTypingIndicator(activeConversationId, isTyping)
  }

  const handleNewChatCreated = (conversationId: string) => {
    queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
    handleConversationSelect(conversationId)
  }

  const handleLeaveConversation = async () => {
    if (!activeConversationId) return
    try {
      await chatApi.leaveConversation(activeConversationId)
      removeConversation(activeConversationId)
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      setActiveConversation(null)
      if (isMobile) {
        setMobileView('list')
      }
    } catch (error) {
      console.error('Failed to leave conversation:', error)
    } finally {
      setLeaveDialogOpen(false)
    }
  }

  // Get current user's participant record for active conversation
  const currentUserParticipant = activeConversation?.participants.find(
    (p) => p.userId === user?.userId
  )

  // Settings mutation
  const settingsMutation = useMutation({
    mutationFn: (settings: import('@api/types/chat').ConversationSettings) =>
      chatApi.updateConversationSettings(activeConversationId!, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
    },
  })

  const handleNotificationModeChange = (mode: NotificationMode) => {
    if (!activeConversationId) return
    settingsMutation.mutate({ notificationMode: mode })
  }

  // Check if active conversation is adventure type
  const isAdventureConversation = activeConversation?.type === ConversationType.Adventure

  if (conversationsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load conversations. Please try again.</Alert>
      </Box>
    )
  }

  const notificationModes: NotificationMode[] = ['Muted', 'NoticeOnly', 'AutoOpen']
  const notificationModeLabels: Record<string, string> = {
    Muted: 'Muted',
    NoticeOnly: 'Notice Only',
    AutoOpen: 'Auto Open',
  }
  const notificationModeDescriptions: Record<string, string> = {
    Muted: 'No notifications from this conversation',
    NoticeOnly: 'Show on notifications icon, not chat badge',
    AutoOpen: 'Pop open chat when new messages arrive',
  }
  const notificationModeColors: Record<string, string> = {
    Muted: '#e57373',
    NoticeOnly: '#bdbdbd',
    AutoOpen: '#66bb6a',
  }

  const serverMode: NotificationMode = resolveNotificationMode(currentUserParticipant?.notificationMode)
  const [localMode, setLocalMode] = useState<NotificationMode | null>(null)
  const currentMode = localMode ?? serverMode
  const currentIndex = Math.max(0, notificationModes.indexOf(currentMode))

  // Sync local override back to server when it changes
  useEffect(() => {
    setLocalMode(null)
  }, [serverMode])

  const handleSwitchClick = (index: number) => {
    if (settingsMutation.isPending) return
    const mode = notificationModes[index]
    setLocalMode(mode)
    handleNotificationModeChange(mode)
  }

  // Render settings dialog
  const trackWidth = 200
  const thumbSize = 30
  const trackPad = 3
  const thumbPositions = [trackPad, (trackWidth - thumbSize) / 2, trackWidth - thumbSize - trackPad]

  const renderSettingsDialog = () => (
    <Dialog
      open={settingsDialogOpen}
      onClose={() => setSettingsDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Conversation Settings</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Notification Mode
        </Typography>
        {/* Tri-state iOS-style switch */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              position: 'relative',
              width: trackWidth,
              height: 36,
              bgcolor: 'action.disabledBackground',
              borderRadius: '18px',
              cursor: settingsMutation.isPending ? 'default' : 'pointer',
              opacity: settingsMutation.isPending ? 0.5 : 1,
            }}
          >
            {/* Colored background fill */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '18px',
                bgcolor: notificationModeColors[currentMode],
                opacity: 0.3,
                transition: 'background-color 0.3s ease',
                pointerEvents: 'none',
              }}
            />
            {/* Click zones */}
            {notificationModes.map((_, i) => (
              <Box
                component="button"
                key={i}
                onClick={() => handleSwitchClick(i)}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: `${(i / 3) * 100}%`,
                  width: '33.33%',
                  height: '100%',
                  zIndex: 2,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  p: 0,
                  m: 0,
                }}
              />
            ))}
            {/* Sliding thumb */}
            <Box
              sx={{
                position: 'absolute',
                top: trackPad,
                left: `${thumbPositions[currentIndex]}px`,
                width: thumbSize,
                height: thumbSize,
                borderRadius: '50%',
                bgcolor: notificationModeColors[currentMode],
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                transition: 'left 0.3s ease, background-color 0.3s ease',
                pointerEvents: 'none',
              }}
            />
          </Box>
          {/* Label */}
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{ color: notificationModeColors[currentMode], transition: 'color 0.3s ease' }}
          >
            {notificationModeLabels[currentMode]}
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {notificationModeDescriptions[currentMode]}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSettingsDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  )

  // Mobile Layout
  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
        }}
      >
        {mobileView === 'list' ? (
          <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
            <ConversationList
              onConversationSelect={handleConversationSelect}
              isLoading={conversationsLoading}
            />
            {/* FAB for new chat */}
            <Fab
              color="primary"
              size="medium"
              onClick={() => setNewChatDialogOpen(true)}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
              }}
            >
              <Add />
            </Fab>
          </Box>
        ) : (
          <>
            {/* Chat header */}
            <ChatHeader
              conversation={activeConversation}
              onBack={handleBack}
              onSettings={() => setSettingsDialogOpen(true)}
              onLeave={() => setLeaveDialogOpen(true)}
            />

            {/* Adventure context banner */}
            {isAdventureConversation && activeConversation?.adventureId && activeConversation?.name && (
              <AdventureContextBanner
                adventureId={activeConversation.adventureId}
                adventureName={activeConversation.name}
              />
            )}

            {/* Messages */}
            {activeConversationId ? (
              <>
                <MessageThread
                  conversationId={activeConversationId}
                  onLoadMore={loadMoreMessages}
                  typingUsers={Array.from(typingUsers.entries()).map(([userId, { userName }]) => ({ userId, userName }))}
                />
                <MessageInput
                  onSend={handleSendMessage}
                  onTyping={handleTyping}
                  onImageUpload={handleImageUpload}
                  disabled={sendMessageMutation.isPending}
                />
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </>
        )}

        <NewChatDialog
          open={newChatDialogOpen}
          onClose={() => setNewChatDialogOpen(false)}
          onChatCreated={handleNewChatCreated}
        />
        {renderSettingsDialog()}
        <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)}>
          <DialogTitle>Leave Conversation</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to leave this conversation? You will no longer receive messages from it.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleLeaveConversation} color="error">Leave</Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  // Desktop Layout
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flex: 1,
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            position: 'relative',
          }}
        >
          <ConversationList
            onConversationSelect={handleConversationSelect}
            isLoading={conversationsLoading}
          />
          {/* FAB for new chat */}
          <Fab
            color="primary"
            size="small"
            onClick={() => setNewChatDialogOpen(true)}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
            }}
          >
            <Add />
          </Fab>
        </Box>

        {/* Main chat area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {activeConversationId ? (
            <>
              <ChatHeader
                conversation={activeConversation}
                onSettings={() => setSettingsDialogOpen(true)}
                onLeave={() => setLeaveDialogOpen(true)}
              />
              {/* Adventure context banner */}
              {isAdventureConversation && activeConversation?.adventureId && activeConversation?.adventureName && (
                <AdventureContextBanner
                  adventureId={activeConversation.adventureId}
                  adventureName={activeConversation.adventureName}
                />
              )}
              <MessageThread
                conversationId={activeConversationId}
                onLoadMore={loadMoreMessages}
                typingUsers={Array.from(typingUsers.entries()).map(([userId, { userName }]) => ({ userId, userName }))}
              />
              <MessageInput
                onSend={handleSendMessage}
                onTyping={handleTyping}
                onImageUpload={handleImageUpload}
                disabled={sendMessageMutation.isPending}
              />
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
              }}
            >
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <Groups sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Box>Select a conversation to start chatting</Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <NewChatDialog
        open={newChatDialogOpen}
        onClose={() => setNewChatDialogOpen(false)}
        onChatCreated={handleNewChatCreated}
      />
      {renderSettingsDialog()}
      <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)}>
        <DialogTitle>Leave Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to leave this conversation? You will no longer receive messages from it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLeaveConversation} color="error">Leave</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
